// Monster sprite registry - per-monster spritesheets generated by
// tools/sprite_pipeline. Each monster directory contains:
//   - move-sheet.png   (384x384, 4 cols x 4 rows of 96x96, rows = down/left/right/up)
//   - attack-sheet.png (384x384, same layout as move)
//   - die-sheet.png    (512x128, 4 cols x 1 row of 128x128)
//
// Texture keys:
//   `<id>__move`   walk animation sheet
//   `<id>__attack` attack animation sheet
//   `<id>__die`    death animation sheet
// where <id> is the monster id (e.g. `mon_skeleton`) — same value as
// `spriteKey` in client/src/data/monsters.json.
//
// Adding a new monster sprite:
//   1. Drop sheets at sprites/monster/<id>/{move,attack,die}-sheet.png
//   2. Copy to client/public/assets/monsters/<id>/
//   3. Add the id to MONSTER_SPRITE_IDS below.

export const MONSTER_MOVE_FRAME_W = 96;
export const MONSTER_MOVE_FRAME_H = 96;
export const MONSTER_DIE_FRAME_W = 128;
export const MONSTER_DIE_FRAME_H = 128;

export type MonsterDir = 'down' | 'left' | 'right' | 'up';
export const MONSTER_DIR_ROW: Record<MonsterDir, number> = {
  down: 0, left: 1, right: 2, up: 3,
};

// Per-monster row overrides for sheets whose row layout differs from the
// default down=0, left=1, right=2, up=3 convention. Add an entry here when a
// generated spritesheet uses a non-standard ordering.
export const MONSTER_DIR_ROW_OVERRIDES: Record<string, Record<MonsterDir, number>> = {
  // Wolf sheet rows are ordered: down, up, left, right (left/right swapped
  // with up vs the default). Without this override, walking left plays the
  // right-facing frames and vice versa.
  mon_wolf: { down: 0, up: 1, left: 2, right: 3 },
};

export function monsterDirRow(id: string, dir: MonsterDir): number {
  const override = MONSTER_DIR_ROW_OVERRIDES[id];
  return override ? override[dir] : MONSTER_DIR_ROW[dir];
}

export const MONSTER_SPRITE_IDS: readonly string[] = [
  'mon_aegis','mon_alquamos','mon_assassin','mon_bahamut','mon_bandit',
  'mon_beam_knight','mon_blade_knight_trainer','mon_blood_soldier',
  'mon_bloody_wolf','mon_bone_knight','mon_budge_dragon','mon_chaos_spawn',
  'mon_class_guardian','mon_cloud_crystal','mon_cursed_mage','mon_cursed_soul',
  'mon_cursed_wizard','mon_cyclops','mon_death_angel','mon_death_angel_dg',
  'mon_death_beam','mon_death_centurion','mon_death_cow','mon_death_cow_dg',
  'mon_death_knight','mon_death_knight_dg','mon_demon_imp','mon_demon_mage',
  'mon_demon_soldier','mon_drakan','mon_eagle','mon_erohim','mon_frost_lord',
  'mon_ghoul','mon_goblin','mon_goblin_king','mon_god_darkness','mon_gorgon',
  'mon_hell_spider','mon_hellhound','mon_hound','mon_hydra','mon_ice_dragon',
  'mon_ice_golem','mon_ice_monster','mon_ice_wolf','mon_iron_knight',
  'mon_iron_wheel','mon_knight_apprentice','mon_kundun','mon_kundun_avatar',
  'mon_larva','mon_lich','mon_lizard_king','mon_medusa','mon_mega_crust',
  'mon_mirage','mon_mirror_self','mon_mummy','mon_muse_elf_trainer',
  'mon_mutant','mon_necron','mon_pearl_shell','mon_phantom_knight',
  'mon_phoenix_dark','mon_poison_bull','mon_poison_shadow','mon_queen_rainer',
  'mon_quicksand','mon_rat','mon_rogue_centurion','mon_scorpion_king',
  'mon_sea_worm','mon_selupan','mon_shipwreck','mon_skeleton',
  'mon_skeleton_archer','mon_skeleton_king','mon_sky_warrior',
  'mon_soul_master_trainer','mon_spider','mon_stone_golem','mon_tantallos',
  'mon_thunder_lich','mon_valkyrie','mon_venom_queen','mon_vepar',
  'mon_werewolf','mon_white_wizard','mon_wolf','mon_yeti','mon_zaikan',
] as const;

export const MONSTER_SPRITE_ID_SET: ReadonlySet<string> = new Set(MONSTER_SPRITE_IDS);

export function monsterMoveKey(id: string): string { return `${id}__move`; }
export function monsterAttackKey(id: string): string { return `${id}__attack`; }
export function monsterDieKey(id: string): string { return `${id}__die`; }

export function monsterMoveUrl(id: string): string { return `/assets/monsters/${id}/move-sheet.png`; }
export function monsterAttackUrl(id: string): string { return `/assets/monsters/${id}/attack-sheet.png`; }
export function monsterDieUrl(id: string): string { return `/assets/monsters/${id}/die-sheet.png`; }

export function hasMonsterSprite(spriteKey: string | undefined | null): boolean {
  return !!spriteKey && MONSTER_SPRITE_ID_SET.has(spriteKey);
}

// Fallback aliases for sprite keys referenced in monsters.json that don't yet
// have their own generated sheets. Maps the missing key to the closest visual
// match so the monster still renders with animations until the real art ships.
export const MONSTER_SPRITE_ALIASES: Record<string, string> = {};

export function resolveMonsterSpriteId(spriteKey: string | undefined | null): string | undefined {
  if (!spriteKey) return undefined;
  if (MONSTER_SPRITE_ID_SET.has(spriteKey)) return spriteKey;
  const aliased = MONSTER_SPRITE_ALIASES[spriteKey];
  if (aliased && MONSTER_SPRITE_ID_SET.has(aliased)) return aliased;
  return undefined;
}

export interface MonsterSpriteEntry {
  id: string;
  moveKey: string;
  attackKey: string;
  dieKey: string;
  moveUrl: string;
  attackUrl: string;
  dieUrl: string;
}

export const MONSTER_SPRITE_ENTRIES: ReadonlyArray<MonsterSpriteEntry> =
  MONSTER_SPRITE_IDS.map((id) => ({
    id,
    moveKey: monsterMoveKey(id),
    attackKey: monsterAttackKey(id),
    dieKey: monsterDieKey(id),
    moveUrl: monsterMoveUrl(id),
    attackUrl: monsterAttackUrl(id),
    dieUrl: monsterDieUrl(id),
  }));

export function monsterAnimKey(
  id: string,
  state: 'walk' | 'attack' | 'die',
  dir?: MonsterDir,
): string {
  if (state === 'die') return `${id}__die`;
  return `${id}__${state}_${dir ?? 'down'}`;
}
